import SafeMJ from "../../utils/SafeMJ";
import compund_interest from "../assets/compound_interest.en.png";

export default function TAEEn() {
  return (
    <>
    <p className="mb-3">
      This Christmas, on top of the classic ads for seductive perfumes and the lottery that never pays out, there's been a real barrage of campaigns from online banks
      and brokers promoting their TAE (the Spanish equivalent of AER). "Come over to Trade Republic and get 2.02% TAE on your cash". "With Revolut, earn up to 2.27% TAE paid daily"...
      But what exactly is this TAE thing?
    </p>
    <p className="mb-3">
      At first glance, it looks like another one of those financial concepts designed more to impress than to explain. As we all know by now, banks and
      economists often use this confusing jargon of acronyms and anglicisms to try to leave people "out of the loop". However, the reality is that behind TAE
      there's nothing especially mysterious. What there is, is elementary maths, and on top of that, a beautiful and surprising relationship with one of the quintessential
      numbers of nature. Today, in a much simpler way than you probably imagine, we'll look at it in detail.
    </p>
    <h2 className="text-xl sm:text-2xl font-bold mt-8 mb-5">1. That famous compound interest</h2>
    <p className="mb-3">
      Imagine you say: "Come on, fine, I'll open one of those interest-bearing accounts people talk about so much. Trade Republic, for example, I've heard it's good", and you put in €100 as initial capital (<SafeMJ after=").">{"\\(C_0\\)"}</SafeMJ>{" "}
      That would be your <b>cash</b>, that is, your uninvested money, always withdrawable.
    </p>
    <p className="mb-3">
      So what happens then? Well, Trade Republic is going to pay you for keeping your money sitting there.
      In the end they use that money behind the scenes, even if in your eyes it's idle, and in return they give you a small reward proportional to your cash called <b>interest</b>. Mind you,
      not every bank does this. As crazy as it may sound, many traditional banks give you nothing (at least not directly) in return.
      But anyway, that's a whole other topic you'll probably want to look into on your own.
    </p>
    <p className="mb-3">
      Under Trade Republic's particular policy, they give 2% annual interest distributed in daily payments (although they don't credit it to your account until the end of the month, but hey, in
      terms of returns it's the same thing). In other words, at the end of the first day you generate the return associated with one three-hundred-sixty-fifth of that 2%, since there are 365 days in a year.
      That is, you would receive:
      <SafeMJ>{"\\[100\\cdot\\frac{2\\%}{365}\\,\\text{€}=100\\cdot\\frac{0.02}{365}\\,\\text{€},\\]"}</SafeMJ>
      so at the end of the first day you would have in your account:
      <div className="hidden sm:block">
        <SafeMJ>{"\\[C_1=100+100\\cdot\\frac{0.02}{365}\\,\\text{€}=C_0\\cdot\\left(1+\\frac{0.02}{365}\\right)\\,\\text{€}.\\]"}</SafeMJ>
      </div>
      <div className="block sm:hidden">
        <SafeMJ>{`
          \\[
          \\begin{aligned}
          C_1&=100+100\\cdot\\frac{0.02}{365}\\,\\text{€} \\\\[6pt]
          &=C_0\\cdot\\left(1+\\frac{0.02}{365}\\right)\\,\\text{€}.
          \\end{aligned}
          \\]
        `}</SafeMJ>
      </div>
      Okay, cool, it's better than nothing. And after two days? Well, here you have to take into account a very important detail: the returns you've received after the first day are already part
      of your capital, and therefore must be taken into account when calculating the interest for the next day. That is, the next interest is calculated based on <SafeMJ after=",">{"\\(C_1\\)"}</SafeMJ>{" "}
      and not on <SafeMJ after=".">{"\\(C_0\\)"}</SafeMJ> This idea of applying the proportional interest to the most recent capital (initial + accumulated returns) is what's called <b>compound interest</b>,
      which I'm sure you've heard so much about lately.
    </p>
    <p className="mb-3">
      As for the exact amounts, it's easy to see that at the end of the second day you'd end up with the following amount in your account:
      <SafeMJ scrollable>{"\\[C_1\\cdot\\left(1+\\frac{0.02}{365}\\right)\\,\\text{€}=C_0\\cdot\\left(1+\\frac{0.02}{365}\\right)^2\\,\\text{€},\\]"}</SafeMJ>
      and as you can already deduce, you'd end the year with:
      <SafeMJ scrollable>{"\\[C_{365}=C_0\\cdot\\left(1+\\frac{0.02}{365}\\right)^{365}\\,\\text{€}.\\]"}</SafeMJ>
    </p>
    <p className="mb-3">
      If you're observant you'll have noticed that the exponent above, the 365, is actually pretty juicy. Imagine we don't stop at one year, that we keep letting the little snowball
      grow and grow... We then know that our capital after <SafeMJ>{"\\(t\\)"}</SafeMJ> years will be given by the function:
      <SafeMJ scrollable>{"\\[C_{365t}=C_0\\cdot\\left(1+\\frac{0.02}{365}\\right)^{365t}\\,\\text{€},\\]"}</SafeMJ>
      which, luckily for us, has exponential form. And as you already know, exponentials end up growing wildly!
      If you don't believe me, look for example at the following evolution of <SafeMJ>{"\\(C_{365t}\\)"}</SafeMJ> for 3 different initial capitals:
      <img
        src={compund_interest}
        alt="Exponential growth of 3 initial capitals thanks to compound interest"
        className="w-max sm:w-[65%] mx-auto my-4"
      />
    </p>
    <p className="mb-3">
      Obviously the chart above is a bit forced, because nobody in their right mind is going to wait 100 years to withdraw their money. But the important thing is that it shows the exponential nature of compound interest and
      that, with a large enough initial capital and a little bit of patience, the little snowball can reach a very considerable size in not that much time. In fact, I invite you to study the
      expression you'd get if, besides starting with the initial capital <SafeMJ after=",">{"\\(C_0\\)"}</SafeMJ> you kept contributing an additional amount every month. You'll see how there the effect of compound interest
      really shows right away...
    </p>
    <p className="mb-3">
      Fantastic, so once all these concepts are understood, which as you've seen aren't very complicated, we have everything ready to tackle TAE. Its definition, as you'll see, turns out to be super simple at this point.
    </p>
    <h2 className="text-xl sm:text-2xl font-bold mt-8 mb-5">2. What is TAE?</h2>
    <p className="mb-3">
      TAE is the answer to a natural question any good saver would ask in the previous scenario: "What's going to be my total
      rate of return at the end of the year?" Or put in a slightly more convoluted way: "What interest would they have to offer me in a single payment at the end
      of the year to replicate the effect of compound interest?". In fact, with this last question it now makes complete sense that the acronym
      TAE stands for <b>Tasa Anual Equivalente</b> (Equivalent Annual Rate).
    </p>
    <p className="mb-3">
      Well, its calculation is practically straightforward. In the previous example:
      <div className="hidden lg:block">
        <SafeMJ>{`
          \\[
          \\begin{aligned}
          \\text{TAE}=\\frac{\\text{Returns}}{\\text{Initial capital}}
          &=\\frac{C_0\\cdot\\left(1+\\frac{0.02}{365}\\right)^{365}-C_0}{C_0} \\\\[6pt]
          &=\\left(1+\\frac{0.02}{365}\\right)^{365}-1\\approx 0.02020,
          \\end{aligned}
          \\]
        `}</SafeMJ>
      </div>
      <div className="block lg:hidden">
        <SafeMJ scrollable>{`
          \\[
          \\begin{aligned}
          \\text{TAE}
          &=\\frac{\\text{Returns}}{\\text{Initial capital}} \\\\[6pt]
          &=\\frac{C_0\\cdot\\left(1+\\frac{0.02}{365}\\right)^{365}-C_0}{C_0} \\\\[6pt]
          &=\\left(1+\\frac{0.02}{365}\\right)^{365}-1\\approx 0.02020,
          \\end{aligned}
          \\]
        `}</SafeMJ>
      </div>
      which is exactly Trade Republic's famous 2.02% TAE.
    </p>
    <p className="mb-3">
    Obviously this formula can be easily generalised, and in fact many texts present it as follows:
      <SafeMJ scrollable>{"\\[\\text{TAE}_{r,n}=\\left(1+\\frac{r}{n}\\right)^{n}-1,\\tag{2.1}\\]"}</SafeMJ>
      where:
      <ul className="list-disc list-inside my-3">
        <li><SafeMJ>{"\\(r\\)"}</SafeMJ> is the so-called <b>Nominal Interest Rate</b> (TIN) expressed as a decimal. The 0.02 in our example.</li>
        <li><SafeMJ>{"\\(n\\)"}</SafeMJ> is the number of interest payments (uniformly distributed) throughout the year. In our case, 365.</li>
      </ul>
    </p>
    <p className="mb-3">
      We also observe that TAE, being a ratio, is dimensionless, and that as expected it doesn't depend on <SafeMJ after=".">{"\\(C_0\\)"}</SafeMJ> That said, just as we saw in the previous section,
      the larger the initial capital, the larger the return in absolute terms, even though not in relative terms. That is, that 2.02% annual return will be the
      same for someone earning €1,000 a month as for a millionaire, it's just that the millionaire will get more out of it, of course.
    </p>
    <p className="mb-3">
      Another observation that's of more interest (pun intended) than it seems is that the TAE we've obtained, 2.02%, is greater than the TIN (2%). And it really makes complete sense, because surely we'll get more return by calculating the daily payments
      on a capital that's a little bit bigger each day than by always doing it based on the initial €100. The first case would be for <SafeMJ after=",">{"\\(n=365\\)"}</SafeMJ> while the second corresponds to <SafeMJ after=",">{"\\(n=1\\)"}</SafeMJ> since
      <div className="hidden sm:block">
        <SafeMJ>{"\\[C_0(1+0.02)=C_0+\\underbrace{C_0\\frac{0.02}{365}}_{\\displaystyle\\text{day 1}}+\\cdots+\\underbrace{C_0\\frac{0.02}{365}}_{\\displaystyle\\text{day 365}}.\\]"}</SafeMJ>
      </div>
      <div className="block sm:hidden">
        <SafeMJ scrollable>{`
          \\[
          \\begin{aligned}
          C_0(1+0.02)&=C_0+\\underbrace{C_0\\frac{0.02}{365}}_{\\displaystyle\\text{day 1}} \\\\[6pt]
          &\\phantom{=}+\\cdots+\\underbrace{C_0\\frac{0.02}{365}}_{\\displaystyle\\text{day 365}}.
          \\end{aligned}
          \\]
        `}</SafeMJ>
      </div>
    </p>
    <p className="mb-3">
      In fact, thanks to the above we now clearly see something more general: <b>the higher the payment frequency, the better off we'll be</b> (higher TAE), since the interest gets added to the capital sooner. Or put another way:
      <SafeMJ scrollable>{"\\[\\left(1+\\frac{r}{n+1}\\right)^{n+1}>\\left(1+\\frac{r}{n}\\right)^{n},\\quad \\forall n\\in\\mathbb{N}.\\]"}</SafeMJ>
      More formally, we can derive the inequality above by expanding with the binomial theorem:
      <SafeMJ scrollable>{`
        \\[
        \\begin{aligned}
        S_n=\\left(1+\\frac{r}{n}\\right)^{n}
        &=\\sum_{k=0}^{n}\\binom{n}{k} \\frac{r^k}{n^k} \\\\[6pt]
        &=1+1+\\sum_{k=2}^{n}\\frac{r^k}{k!}\\cdot\\frac{(n-1)\\cdots(n-k+1)}{n^{k-1}} \\\\[6pt]
        &=2+\\sum_{k=2}^{n}\\frac{r^k}{k!}\\cdot\\left(1-\\frac{1}{n}\\right)\\cdots\\left(1-\\frac{k-1}{n}\\right).
        \\end{aligned}
        \\]
      `}</SafeMJ>
    </p>
    <p className="mb-3">
      So, if we increase <SafeMJ>{"\\(n\\)"}</SafeMJ> to <SafeMJ>{"\\(n+1\\)"}</SafeMJ> we'll be making each factor <SafeMJ>{"\\(\\bigl(1-\\frac{j}{n}\\bigr)\\)"}</SafeMJ> bigger and at the same time adding a new positive term,
      so the sequence <SafeMJ>{"\\(S_n\\)"}</SafeMJ> is indeed strictly increasing.
    </p>
    <p className="mb-3">
      What's more, you can already imagine the next natural question that should come to mind now. If TAE grows as we increase the payment frequency, what happens if I ask them to pay me interest every hour?
      And every minute? Why not every millisecond? What if I end up getting rich after all?
    </p>
    <h2 className="text-xl sm:text-2xl font-bold mt-8 mb-5">3. Euler's number</h2>
    <p className="mb-3">
      Unfortunately for you, the mathematician Jacob Bernoulli already studied the sequence <SafeMJ>{"\\(S_n\\)"}</SafeMJ> in the 17th century and confirmed that it didn't go off to infinity, but rather converged...
    </p>
    <p className="mb-3">
      To see this, let's start with the particular case of <SafeMJ after=",">{"\\(r=1\\)"}</SafeMJ> that is, with a TIN that would double our money by the end of the year if we didn't apply compound interest.
      Then we can bound the sequence as follows:
      <SafeMJ scrollable>{`
        \\[
        \\begin{aligned}
        S_n&=2+\\sum_{k=2}^{n}\\frac{1}{k!}\\cdot\\left(1-\\frac{1}{n}\\right)\\cdots\\left(1-\\frac{k-1}{n}\\right) \\\\[6pt]
        &\\le 2+\\sum_{k=2}^{n}\\frac{1}{k!}\\le 2+\\sum_{k=1}^{n-1}\\frac{1}{2^k}.
        \\end{aligned}
        \\]
      `}</SafeMJ>
    </p>
    <p className="mb-3">
      In fact, the sum at the end is a geometric sum, for which we know a closed-form expression. In this case, for a geometric sum with ratio <SafeMJ>{"\\(R=\\frac{1}{2}\\)"}</SafeMJ> and
      first term <SafeMJ after=",">{"\\(a_1=\\frac{1}{2}\\)"}</SafeMJ> we have:
      <SafeMJ scrollable>{"\\[\\sum_{k=1}^{n-1}\\frac{1}{2^k}=a_1\\cdot\\frac{R^{n-1}-1}{R-1}=1-\\frac{1}{2^{n-1}}<1,\\]"}</SafeMJ>
      which implies that <SafeMJ after=".">{"\\(S_n<3\\)"}</SafeMJ>
    </p>
    <p className="mb-3">
      And that's it. Since <SafeMJ>{"\\(S_n\\)"}</SafeMJ> is an increasing sequence bounded above, it must necessarily converge. And in fact, if we look back,
      it must do so to a number between 2 and 3. That limit is, precisely, how the super famous Euler's number is defined:
      <SafeMJ>{"\\[e:=\\lim_{n\\rightarrow\\infty}\\left(1+\\frac{1}{n}\\right)^{n}\\tag{3.1}.\\]"}</SafeMJ>
      Pretty wild, huh? Nowadays it's indisputable that the number <SafeMJ>{"\\(e\\)"}</SafeMJ> is a key tool in mathematics, with applications in complex
      analysis, probability, and even number theory. That its origin was the study of compound interest, something so human and everyday, is beautiful to say the least, right?
    </p>
    <p className="mb-3">
      Before we finish, remember we still have one pending case. What happens if <SafeMJ after="?">{"\\(0\\le r<1\\)"}</SafeMJ>{" "}
      Fortunately, the answer is still very pleasing to the eye:
      <SafeMJ>{"\\[\\lim_{n\\rightarrow\\infty}\\left(1+\\frac{r}{n}\\right)^{n}=e^r.\\]"}</SafeMJ>
      Even if you read on lots of blogs or ChatGPT tells you that the identity above follows easily from <SafeMJ after=",">{"\\((3.1)\\)"}</SafeMJ> I'm telling you it doesn't. At least not
      without using the machinery of functions... To prove it in a more elementary way, staying down in the mud of sequences, you have to be quite careful. I'll leave you <a href="https://www.youtube.com/watch?v=FZEKjsFZfk4"><u>here</u></a> a
      very rigorous proof in case you're interested. Either way, the important thing is that the equality above is true. Trust me.
    </p>
    <p className="mb-3">
      With this we can put the finishing touch on the article and observe the relationship between TAE and Euler's number from expression <SafeMJ after=".">{"\\((2.1)\\)"}</SafeMJ>{" "}
      As you'll have already noticed, we have that:
      <SafeMJ>{"\\[\\boxed{\\lim_{n\\rightarrow\\infty}\\text{TAE}_{r,n}=e^r-1}\\]"}</SafeMJ>
    </p>
    </>
  )
}
